import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiShare2, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import AppModal from '../common/AppModal';
import { formatApiError } from '../../utils/formatters';
import './sharing.css';

/**
 * 공유 본문 — 받을 사람을 찾아 고르고(LiveSQL 을 써 본 사람만), 지금 누구에게 건넸는지 보고 거둔다.
 * 창(ShareDialog, 대시보드)과 탭(연결 관리의 「연결 공유 관리」)이 함께 쓴다. 무엇을 어떻게 건넬지(방식·읽기 전용 등)는
 * `options` 로 받아 그 칸만 다르게 그린다.
 *
 * @param api        sharesApi(...)
 * @param load       () => Promise<공유 목록>   지금 건넨 것
 * @param submit     (usrids) => Promise        건네기
 * @param options    선택 칸(JSX) — 방식 고르기 등
 * @param hint       설명 문구
 * @param resetKey   바뀌면(창이 열리거나 대상이 바뀌면) 고른 사람·검색어를 비우고 다시 읽는다
 * @param onClose    있으면 아래에 「닫기」
 */
export function SharePanel({ api, load, submit, options, hint, ownerUsrid, resetKey, onClose }) {
  const { t } = useTranslation();
  const [existing, setExisting] = useState([]);
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  // load·submit 은 부르는 쪽이 매 렌더 새로 만드는 함수다 — 그것에 매달려 초기화하면 부모가 다시 그릴 때마다
  // 검색어가 지워지고, 한글 조합 중에 지워지면 조합 중이던 글자가 남는다(2026-10-03 신고). 최신 것만 ref 로 쥔다
  const loadRef = useRef(load);
  loadRef.current = load;
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const reload = useCallback(async () => {
    try { setExisting(await loadRef.current()); } catch (err) { setExisting([]); toast.error(formatApiError(err, t)); }
  }, [t]);
  // 초기화는 **대상이 바뀔 때만**
  useEffect(() => {
    setPicked([]);
    setQ('');
    setFound([]);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setFound([]); return undefined; }
    timer.current = setTimeout(async () => {
      try {
        const rows = await api.searchUsers(term);
        setFound(rows.filter(u => u.usrid !== ownerUsrid && !picked.some(p => p.usrid === u.usrid)));
      } catch (_e) { setFound([]); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, api, picked, ownerUsrid]);

  const doSubmit = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    try {
      await submitRef.current(picked.map(p => p.usrid));
      toast.success(t('common:share.sent', { n: picked.length }));
      setPicked([]);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err, t));
    } finally { setBusy(false); }
  };
  const revoke = async (s) => {
    try { await api.remove(s.id); await reload(); } catch (err) { toast.error(formatApiError(err, t)); }
  };

  return (
    <>
      <div className="share-body">
        {hint && <div className="share-hint">{hint}</div>}
        {options}
        <div className="share-label">{t('common:share.to')}</div>
        <div className="share-chips">
          {picked.length === 0 && <span className="share-muted">{t('common:share.nobodyPicked')}</span>}
          {picked.map(p => (
            <span key={p.usrid} className="share-chip" title={p.login || p.usrid}>
              {p.name}
              <button type="button" aria-label={t('common:share.unpick')} onClick={() => setPicked(ps => ps.filter(x => x.usrid !== p.usrid))}><FiX size={10} /></button>
            </span>
          ))}
        </div>
        <input type="text" className="share-search" placeholder={t('common:share.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
        {found.length > 0 && (
          <div className="share-found">
            {found.map(u => (
              <button key={u.usrid} type="button" className="share-found-item" onClick={() => { setPicked(ps => [...ps, u]); setQ(''); setFound([]); }}>
                <b>{u.name}</b> <span className="share-muted">{u.login}</span>
              </button>
            ))}
          </div>
        )}
        {q.trim().length >= 2 && found.length === 0 && <div className="share-muted">{t('common:share.noMatch')}</div>}

        <div className="share-label">{t('common:share.current')}</div>
        {existing.length === 0 && <div className="share-muted">{t('common:share.noneYet')}</div>}
        {existing.length > 0 && (
          <table className="share-table">
            <tbody>
              {existing.map(s => (
                <tr key={s.id}>
                  <td>{(s.person && s.person.name) || s.toUsrid}</td>
                  <td>{t(`common:share.kind.${s.kind}`)}{s.kind === 'conn-use' ? ` · ${s.options && s.options.readOnly === false ? t('common:share.writeAllowed') : t('common:share.readOnly')}` : ''}</td>
                  <td><span className={`share-status ${s.status}`}>{t(`common:share.status.${s.status}`)}</span></td>
                  <td className="share-right">
                    {s.kind !== 'conn-copy' || s.status === 'pending'
                      ? <button type="button" className="app-btn" onClick={() => revoke(s)}>{t('common:share.revoke')}</button>
                      : <span className="share-muted" title={t('common:share.copyDoneHint')}>{t('common:share.copyDone')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="share-footer">
        {onClose && <button type="button" className="app-btn" onClick={onClose}>{t('common:ui.close')}</button>}
        <button type="button" className="app-btn app-btn-primary" onClick={doSubmit} disabled={busy || picked.length === 0}>
          <FiShare2 size={12} /> {t('common:share.send')}
        </button>
      </div>
    </>
  );
}

/** 공유 창 — 대시보드가 쓴다(연결은 연결 관리의 「연결 공유 관리」 탭) */
export default function ShareDialog({ open, onClose, title, ...rest }) {
  const [openSeq, setOpenSeq] = useState(0);
  useEffect(() => { if (open) setOpenSeq(n => n + 1); }, [open]);
  return (
    // className 을 주면 기본 창 클래스(배경·테두리·그림자)가 빠진다 — 기본을 함께 준다(배경이 투명해 뒤와 겹쳤다)
    <AppModal open={open} onClose={onClose} icon={<FiShare2 size={14} />} title={title} className="app-modal-default share-modal">
      {open && <SharePanel {...rest} resetKey={openSeq} onClose={onClose} />}
    </AppModal>
  );
}
